"use client";
import { useEffect, useState, useRef } from "react";
import { useRouter, usePathname } from "next/navigation";
import Link from "next/link";
import { ChevronDown } from "lucide-react";

type NavItem = {
  label: string;
  href?: string;
  value?: string;
};

interface MobileNavProps {
  items: NavItem[];
  initialSelectedItem?: string;
  className?: string;
  label?: string;
  placeholder?: string;
  isOption?: boolean;
  onSelect?: (value: string) => void;
  maxOptions?: number;
  footerText?: string;
  footerLink?: string;
  size?: 'small' | 'medium' | 'large';
}

const sizeStyles = {
  large: {
    wrapper: 'px-[22px] py-4 min-w-[220px]',
    item: 'px-6 py-4 text-base',
    chevron: 'h-5 w-5',
    label: 'text-sm',
  },
  medium: {
    wrapper: 'px-4 py-2 min-w-[180px]',
    item: 'px-4 py-2 text-sm',
    chevron: 'h-4 w-4',
    label: 'text-xs',
  },
  small: {
    wrapper: 'px-2 py-1 min-w-[140px]',
    item: 'px-2 py-1 text-xs',
    chevron: 'h-3 w-3',
    label: 'text-xs',
  },
};

const MobileDropDown = ({
  items,
  initialSelectedItem,
  className = "",
  label,
  placeholder,
  isOption = false,
  onSelect,
  maxOptions,
  footerText,
  footerLink,
  size = 'large',
}: MobileNavProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [selectedItem, setSelectedItem] = useState<string>(
    initialSelectedItem || (placeholder ? "" : items[0]?.label || "")
  );

  const styles = sizeStyles[size];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!isOption) {
      const currentItem = items.find((item) => item.href === pathname);
      if (currentItem) {
        setSelectedItem(currentItem.label);
      }
    } else if (initialSelectedItem) {
      // For option dropdowns, find the item that matches the initial value
      const matchingItem = items.find((item) => item.value === initialSelectedItem);
      if (matchingItem) {
        setSelectedItem(matchingItem.label);
      }
    }
  }, [pathname, items, isOption, initialSelectedItem]);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleItemClick = (item: NavItem) => {
    setSelectedItem(item.label);
    setIsOpen(false);
    
    if (onSelect) {
      onSelect(item.value || "" );
    } else if (item.href) {
      router.push(item.href);
    }
  };

  // Remove filtering - show all items including selected one
  const dropdownItems = items;

  // Limit the number of options shown if maxOptions is specified
  const limitedItems = maxOptions ? dropdownItems.slice(0, maxOptions) : dropdownItems;

  //focus:ring-blue-500 focus:ring-2

  return (
    // <div className={`relative w-full max-w-md ${className}`}>
    //   <div
    //     className="flex cursor-pointer items-center justify-between rounded-t-md border border-gray-300 bg-white p-4"
    //     onClick={toggleDropdown}
    //   >
    //     <span className="font-medium">{selectedItem}</span>
    //     <ChevronDown
    //       className={`h-5 w-5 transition-transform duration-200 ${
    //         isOpen ? "rotate-180" : ""
    //       }`}
    //     />
    //   </div>

    //   {isOpen && (
    //     <div className="absolute z-10 w-full border border-gray-300 bg-white">
    //       {items.map((item) => (
    //         item.label !== selectedItem && (
    //           <button
    //             key={item.label}
    //             className="block w-full border-t-0 border-gray-200 px-4 py-3 text-left hover:bg-gray-50"
    //             onClick={() => handleItemClick(item)}
    //           >
    //             {item.label}
    //           </button>
    //         )
    //       ))}
    //     </div>
    //   )}
    // </div>
    <div className={`relative w-full ${className}`} ref={dropdownRef}>
      <div
        className={`flex cursor-pointer items-center justify-between border border-[#121212] bg-white ${styles.wrapper} ${
          isOpen ? "rounded-t-md" : "rounded-md"
        }`}
      >
        <div
          className="flex items-center justify-between w-full"
          onClick={toggleDropdown}
        >
          <div className={`flex items-center gap-2 font-medium ${styles.label}`}>
            {label && <span className="text-borderGray">{label}</span>}
            <span className={`font-medium ${!selectedItem && placeholder ? 'text-inactiveGray' : ''}`}>
              {selectedItem || placeholder}
            </span>
          </div>
          <ChevronDown
            className={`${styles.chevron} transition-transform duration-200 ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </div>
      </div>

      {isOpen && (
        <div className="absolute z-10 w-full border border-t-0 border-gray-300 bg-white rounded-b-md max-h-80 flex flex-col">
          <div className="border-b border-gray-300"></div>
          <div className="overflow-y-auto flex-1">
            {limitedItems.map((item) => (
              <button
                key={item.label}
                className={`block w-full text-left hover:bg-gray-50 ${styles.item} ${
                  item.label === selectedItem ? 'bg-orange/10 text-orange' : ''
                }`}
                onClick={() => handleItemClick(item)}
               aria-label={`Select ${item.label}`}>
                {item.label}
              </button>
            ))}
            {footerText && (
            <>
              <Link
                href={footerLink || ""}
                className={`block w-full text-center hover:bg-gray-50 text-orange font-medium ${styles.item}`}
                onClick={() => setIsOpen(false)}
                prefetch={false}
              >
                {footerText}
              </Link>
            </>
          )}
          </div>
          
        </div>
      )}
    </div>
  );
};

export default MobileDropDown;
